import Keycloak from 'keycloak-js'

const runtimeConfig = window.LOCKY_CONFIG || {}
const envConfig = import.meta.env

const DEFAULT_KEYCLOAK_SERVER = 'https://keycloak.vduval.com'
const DEFAULT_KEYCLOAK_REALM = 'locky'
const DEFAULT_KEYCLOAK_WEBAPP_CLIENTID = 'locky-webapp'

function normalizeUrl(url, fallbackUrl, defaultProtocol = 'https:') {
  const value = `${url ?? ''}`.trim()
  if (!value) return fallbackUrl

  const protocol = defaultProtocol.endsWith(':') ? defaultProtocol : `${defaultProtocol}:`
  const valueWithProtocol = /^[a-z][a-z0-9+.-]*:\/\//i.test(value)
    ? value
    : `${protocol}//${value}`

  return valueWithProtocol.replace(/\/+$/, '')
}

const configuredPublicUrl =
  runtimeConfig.LOCKY_PUBLIC_URL
  || envConfig.LOCKY_PUBLIC_URL
  || envConfig.VITE_LOCKY_PUBLIC_URL

const appBaseUrl = normalizeUrl(configuredPublicUrl, window.location.origin, 'https:')

function appUrl(path = '/') {
  return new URL(`${path}`.replace(/^\/+/, ''), `${appBaseUrl}/`).href
}

function currentAppPath() {
  const basePath = new URL(`${appBaseUrl}/`).pathname.replace(/\/$/, '')
  const currentPath = window.location.pathname
  const currentSuffix = `${window.location.search}${window.location.hash}`

  if (basePath && currentPath === basePath) return `/${currentSuffix}`
  if (basePath && currentPath.startsWith(`${basePath}/`)) {
    return `${currentPath.slice(basePath.length)}${currentSuffix}`
  }
  return `${currentPath}${currentSuffix}`
}

function withRedirectUri(options, path = currentAppPath()) {
  return {
    redirectUri: appUrl(path),
    ...(options || {}),
  }
}

const config = {
  url: normalizeUrl(
    runtimeConfig.KEYCLOAK_SERVER
      || envConfig.KEYCLOAK_SERVER
      || envConfig.VITE_KEYCLOAK_URL,
    DEFAULT_KEYCLOAK_SERVER,
  ),
  realm:
    runtimeConfig.KEYCLOAK_REALM
    || envConfig.KEYCLOAK_REALM
    || envConfig.VITE_KEYCLOAK_REALM
    || DEFAULT_KEYCLOAK_REALM,
  clientId:
    runtimeConfig.KEYCLOAK_WEBAPP_CLIENTID
    || envConfig.KEYCLOAK_WEBAPP_CLIENTID
    || envConfig.VITE_KEYCLOAK_CLIENT
    || DEFAULT_KEYCLOAK_WEBAPP_CLIENTID,
}

// Le check-sso au boot peut entraîner une redirection complète vers Keycloak
// si silentCheckSsoRedirectUri n'est pas configuré et que checkLoginIframe est false.
// On opt-in via VITE_KEYCLOAK_CHECK_SSO=true (défaut: désactivé pour éviter
// toute redirection au boot). L'utilisateur déclenche l'auth via le bouton "Se connecter".
const CHECK_SSO_ON_BOOT = import.meta.env.VITE_KEYCLOAK_CHECK_SSO === 'true'

let kc = null
let refreshTimer = null

export function getKeycloak() { return kc }

export async function initKeycloak() {
  kc = new Keycloak(config)
  const initOptions = {
    pkceMethod: 'S256',
    checkLoginIframe: false,
  }
  if (CHECK_SSO_ON_BOOT) {
    initOptions.onLoad = 'check-sso'
    initOptions.silentCheckSsoRedirectUri = appUrl('/silent-check-sso.html')
  }

  let authenticated = false
  try { authenticated = await kc.init(initOptions) }
  catch (err) { console.warn({message: 'Keycloak init failed', err}); authenticated = false }

  scheduleRefresh()
  return authenticated
}

function scheduleRefresh() {
  if (refreshTimer) clearInterval(refreshTimer)
  refreshTimer = setInterval(async () => {
    try { await kc?.updateToken(30) } catch { /* ignore, will be caught on next API call */ }
  }, 10_000)
}

export function login(options) { return kc?.login(withRedirectUri(options)) }
export function register(options) { return kc?.register(withRedirectUri(options)) }
export function logout(options) { return kc?.logout(withRedirectUri(options, '/')) }
export function accountManagement(options) { return kc?.accountManagement(withRedirectUri(options)) }

export function getToken() { return kc?.token }
export function isAuthenticated() { return Boolean(kc?.authenticated) }
export function getProfile() {
  if (!kc?.tokenParsed) return null
  return {
    sub: kc.tokenParsed.sub,
    username: kc.tokenParsed.preferred_username,
    email: kc.tokenParsed.email,
    name: kc.tokenParsed.name,
  }
}
